<template>
  <div class="form-control mail-input-wrapper recipients-input">
    <!-- Destinatari come chip all'interno dell'input -->
    <div class="recipients">
      <span
        v-for="recipient in recipients"
        :key="recipient.email"
        class="recipient-chip"
        :title="recipient.email"
      >
        <span v-if="recipient.name || recipient.surname">
          {{ recipient.name ?? '' }} {{ recipient.surname ?? '' }}
        </span>
        <span v-else>
          {{ recipient.email }}
        </span>
        <button @click="removeRecipient(recipient.email)">×</button>
      </span>
      <input
        type="text"
        v-model="searchQuery"
        class="input-field"
        :placeholder="label"
        @input="filterResults"
        @keydown="handleKeyDown"
        v-on:blur="() => addManualRecipient()"
      />
    </div>
    <!-- Lista dei risultati -->
    <ul v-if="filteredResults.length" class="dropdown" >
      <li
        v-for="(item, index) in filteredResults"
        :key="item.id || item.name"
        @mousedown="preventDefault"
        @click="selectItem(item)"
        :class="{active: index === highlightedIndex }"
        :ref="el => (listItems[index] = el)"
      >
        <span v-if="item.email" v-html="highlightQuery((item.name ?? '') + ' ' + (item.surname ?? '')  + ' - ' + item.email)"></span>
        <span v-else>
          {{ item.name }} ({{ item.contacts_count ?? item.contacts.length }} membri)
        </span>
      </li>
    </ul>
  </div>
</template>

<script setup>
  import * as addressbookService from "@/services/addressbook.service";
  import { ref, reactive } from "vue";

  let props = defineProps({
    label: '',
    contacts: { type: Array, required: true }, // Contatti disponibili
    groups: { type: Array, required: true },  // Gruppi disponibili
    recipients_emails: { type: Array, required: false },
    account_id: { type: String, required: true }
  });

  let emit = defineEmits(["updateRecipients"]);

  // Espone la funzione per il genitore
  defineExpose({
    loadRecipients
  });

  let searchQuery = ref("");          // Testo inserito dall'utente
  let recipients = ref([]);           // Lista dei destinatari aggiunti
  let filteredResults = ref([]);      // Risultati filtrati
  let highlightedIndex = ref(-1);
  const listItems = []; // Array per raccogliere i riferimenti agli elementi

  // Filtra i contatti e gruppi in base alla query
  function filterResults() {
    if (!searchQuery.value.trim()) {
      filteredResults.value = [];
      return;
    }

    const query = searchQuery.value.toLowerCase();

    const contactResults = props.contacts
      .filter(contact =>
        !recipients.value.some(r => r.email === contact.email) && // Evita duplicati
        (contact.name + " " + contact.surname + " " + contact.email).toLowerCase().includes(query)
      );

    const groupResults = props.groups
      .filter(group =>
        !(group.contacts && group.contacts.every(contact => recipients.value.some(r => r.email === contact.email))) && // Evita duplicati
        group.name.toLowerCase().includes(query)
      );

    filteredResults.value = [...contactResults, ...groupResults];

    highlightedIndex.value = filteredResults.value.length > 0 ? 0 : -1;
  }

  // Evidenzia la parte cercata
  function highlightQuery(text) {
    const query = searchQuery.value.trim();
    if (!query) return text;

    const regex = new RegExp(`(${query})`, "gi");
    return text.replace(regex, "<strong>$1</strong>");
  }

  function preventDefault(event) {
    event.preventDefault();
  }

  // Aggiungi destinatario selezionato
  async function selectItem(item) {
    if (item.email) {
      // Aggiungi singolo contatto
      addRecipient(item);
    } else if (item.id) {
      // Aggiungi membri di un gruppo
      if(!item.contacts)
      {
        const groupMembers = await addressbookService.getContacts(props.account_id, item.id);
        item.contacts = groupMembers;
      }

      item.contacts.forEach(contact => addRecipient(contact));
    }
    searchQuery.value = "";
    highlightedIndex.value = -1;
    filteredResults.value = [];
  }

  // Aggiungi destinatario manualmente
  function addManualRecipient(address) {
    let value = address ?? searchQuery.value.trim();
    let emails = value.split(";");

    for (let email of emails) {
      email = email.trim();
      if (email && !recipients.value.some(r => r.email === email)) {
        addRecipient({ email });
      }
    }

    searchQuery.value = "";
    filteredResults.value = [];
  }

  // Aggiungi un destinatario all'array
  function addRecipient(contact) {
    if (!recipients.value.some(r => r.email === contact.email)) {
      recipients.value.push(contact);
      emit("updateRecipients", recipients.value);
    }
  }

  // Gestisci il tasto Backspace
  function handleBackspace(event) {
    if (!searchQuery.value && recipients.value.length) {
      // Rimuovi l'ultimo destinatario se il campo è vuoto
      removeRecipient(recipients.value[recipients.value.length - 1].email);
    }
  }

  // Rimuovi un destinatario
  function removeRecipient(email) {
    recipients.value = recipients.value.filter(r => r.email !== email);
    emit("updateRecipients", recipients.value);
  }

  function loadRecipients(recipients_emails, contacts_list) {
    console.log(recipients_emails); // DEBUG
    if(recipients_emails) {
      recipients_emails.forEach(email => {
        let contact = contacts_list.find(c => c.email === email);

        if(contact) {
          addRecipient(contact);
        }
        else {
          addManualRecipient(email);
        }
      });
    }
  }

  function handleKeyDown(event) {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        if (highlightedIndex.value < filteredResults.value.length - 1) {
          highlightedIndex.value++;
          scrollToHighlighted();
        }
        break;
      case "ArrowUp":
        event.preventDefault();
        if (highlightedIndex.value > 0) {
          highlightedIndex.value--;
          scrollToHighlighted();
        }
        break;
      case "Enter":
        if (highlightedIndex.value !== -1) {
          // Seleziona il suggerimento attivo
          selectSuggestion(highlightedIndex.value);
        } else {
          // Conferma input manuale
          addManualRecipient();
        }
        event.preventDefault();
        break;
      case "Escape":
        // Nascondi i suggerimenti
        filteredResults.value = [];
        highlightedIndex.value = -1;
        break;
      case "Backspace":
        // Nascondi i suggerimenti
        handleBackspace(event);
        break;
    }
  }

  function selectSuggestion() {
    selectItem(filteredResults.value[highlightedIndex.value])
  }

  function scrollToHighlighted() {
    const highlightedItem = listItems[highlightedIndex.value];
    if (highlightedItem) {
      highlightedItem.scrollIntoView({
        behavior: "smooth", // Scrolling fluido
        block: "nearest",   // Posiziona l'elemento più vicino
      });
    }
  }

  // init
</script>

<style scoped>
.mail-input-wrapper {
  /* position: relative; */
  width: 100%;
}

.recipients-input {
  display: flex;
  flex-wrap: wrap;
  /* border: 1px solid #ccc; */
  padding: 2px;
  /* border-radius: 5px; */
  /* background: #fff; */
  align-items: center;
}

.recipients {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  flex-grow: 1;
}

.recipient-chip {
  background: var(--archi-primary);
  color: white;
  padding: 0px 10px;
  margin: 3px 3px;
  border-radius: 15px;
  display: flex;
  align-items: center;
}

.recipient-chip button {
  background: none;
  border: none;
  color: white;
  margin-left: 5px;
  cursor: pointer;
}

.input-field {
  flex-grow: 1;
  border: none;
  outline: none;
  background-color: transparent;
  min-width: 100px;
  margin: 0px 0;
}

.dropdown {
  list-style: none;
  margin: 0;
  padding: 0;
  border: 1px solid #ccc;
  background: #fff;
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 10;
  max-height: 200px;
  overflow-y: auto;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
}

.dropdown li {
  padding: 8px;
  cursor: pointer;
}

li.active {
  background-color: #e3e3e3;
}

.dropdown li:hover {
  background-color: #f0f0f0;
}

.dropdown strong {
  font-weight: bold;
  color: #007bff;
}

.form-control {
  height: auto;
}
</style>
